import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Users, Target, Calendar, Mail, Lock, Eye, EyeOff, ArrowLeft, Loader2, Heart, Compass, Mic } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import BrandWordmark from './brand/BrandWordmark';

const API = '/api';
const RETURN_TO_STORAGE_KEY = 'dp_return_to';
const normalizeReturnToPath = (value) => {
  if (typeof value !== 'string' || !value.startsWith('/')) return '';

  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return '';
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '';
  }
};

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const requestedReturnTo = normalizeReturnToPath(searchParams.get('returnTo') || '');
  const storedReturnTo = normalizeReturnToPath(
    typeof window !== 'undefined' ? sessionStorage.getItem(RETURN_TO_STORAGE_KEY) || '' : ''
  );
  const postAuthDestination = requestedReturnTo || storedReturnTo || '/';

  useEffect(() => {
    if (requestedReturnTo) {
      sessionStorage.setItem(RETURN_TO_STORAGE_KEY, requestedReturnTo);
    }
  }, [requestedReturnTo]);



  const { loginWithPassword, checkAuth, isAuthenticated, isAdmin, authLoading, logout } = useAuth();
  const [mode, setMode] = useState('login'); // 'login', 'forgot'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guestGuides, setGuestGuides] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // Only auto-redirect when the current session is actually an admin session.
  // Otherwise a non-admin session can bounce between "/" and "/login".
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isAdmin) return;

    const nextPath = postAuthDestination;
    sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
    window.location.replace(nextPath);
  }, [authLoading, isAuthenticated, isAdmin, postAuthDestination]);

    // Guest guides remain available on the internal login page.
    useEffect(() => {
      const fetchData = async () => {
        try {
          const guidesRes = await fetch(`${API}/guest-guides/public`);

          if (guidesRes.ok) {
            const guides = await guidesRes.json();
            setGuestGuides(guides);
          } else {
            console.error('Failed to fetch guest guides:', guidesRes.status);
          }
        } catch (error) {
          console.error('Failed to fetch data:', error);
        }
      };
      fetchData();
    }, []);

  // Read mode from URL params on mount
  useEffect(() => {
    const urlMode = searchParams.get('mode');
    if (urlMode === 'login' || urlMode === 'forgot') {
      setMode(urlMode);
    }
  }, [searchParams]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.email || !formData.password) {
        toast.error('Email and password are required.');
        return;
      }

      await loginWithPassword(formData.email, formData.password);
      const signedIn = await checkAuth();
      if (!signedIn) {
        throw new Error('Sign-in did not complete. Please try again.');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };


  const handleForgotPassword = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    const cleanEmail = (formData.email || '').trim().toLowerCase();
    const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) throw error;

    toast.success('Password reset email sent. Check your inbox.');
    setMode('login');
  } catch (err) {
    toast.error(err?.message || 'Failed to send reset email');
  } finally {
    setLoading(false);
  }
};


  const resetForm = () => {
    setFormData({ email: '', password: '' });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="mb-6 flex justify-center">
            <BrandWordmark textClassName="text-5xl md:text-6xl" />
          </div>
          <p className="mb-8 text-sm uppercase tracking-[0.3em] text-muted-foreground">
            A Brotherhood for Christian Dads
          </p>
          <p className="mb-16 mx-auto max-w-[44rem] text-xl leading-relaxed text-muted-foreground">
            <span className="block text-balance">
              Dad Partners brings together fathers with children at home who want
              real brotherhood as they navigate the pressures of parenting,
              marriage, and work.
            </span>
            <span className="mt-2 block text-balance">
              Through honest conversation, shared wisdom, and support from other
              dads, members grow stronger together.
            </span>
          </p>

        </div>

        {/* Auth Card */}
        <div className="bg-charcoal-light rounded-2xl border border-charcoal-lighter p-8 max-w-md mx-auto">
          {!authLoading && isAuthenticated && !isAdmin && (
            <div className="mb-6 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-4">
              <p className="text-sm font-medium text-yellow-300">This account does not have Command Center access.</p>
              <p className="mt-1 text-sm text-yellow-100/80">
                Sign out here, then log in with your admin account.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => logout()}
                className="mt-4 border-yellow-500/40 text-yellow-200 hover:bg-yellow-500/10"
              >
                Sign Out
              </Button>
            </div>
          )}

          {/* Login Mode */}
          {mode === 'login' && (
            <>
              <h2 className="text-2xl font-bold text-white mb-6 text-center">Welcome Back</h2>

              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="pl-10 bg-charcoal border-charcoal-lighter text-white"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="pl-10 pr-10 bg-charcoal border-charcoal-lighter text-white"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => { resetForm(); setMode('forgot'); }}
                  className="text-sm text-aqua hover:underline"
                >
                  Forgot password?
                </button>

                <Button type="submit" disabled={loading} className="w-full bg-aqua hover:bg-aqua-dark text-charcoal font-semibold py-6">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
                </Button>
              </form>

              <div className="mt-6 flex items-center justify-end text-sm">
                <button onClick={() => { resetForm(); setMode('forgot'); }} className="text-aqua hover:underline">
                  Forgot password?
                </button>
              </div>
            </>
          )}

          {/* Forgot Password Mode */}
          {mode === 'forgot' && (
            <>
              <button onClick={() => setMode('login')} className="flex items-center gap-2 text-gray-400 hover:text-white mb-4">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h2 className="text-2xl font-bold text-white mb-2 text-center">Reset Password</h2>
              <p className="text-gray-400 text-center mb-6">Enter your email and we'll send you a reset link</p>

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="pl-10 bg-charcoal border-charcoal-lighter text-white"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-aqua hover:bg-aqua-dark text-charcoal font-semibold py-6">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
                </Button>
              </form>
            </>
          )}

          {/* Email Confirmation Mode */}
          {mode === 'confirm-email' && (
            <>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-aqua/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-aqua" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">Check your inbox!</h2>
                <p className="text-gray-300 mb-2">
                  We sent a confirmation link to:
                </p>
                <p className="text-aqua font-semibold mb-6">
                  {formData.email}
                </p>
                <p className="text-gray-400 text-sm mb-6">
                  Click the link in the email to activate your account, then come back here to sign in.
                </p>
                <Button
                  onClick={() => { resetForm(); setMode('login'); }}
                  className="w-full bg-aqua hover:bg-aqua-dark text-charcoal font-semibold py-6"
                >
                  Back to Sign In
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-6 mt-16">
          <div className="bg-charcoal-light rounded-xl p-6 border border-charcoal-lighter">
            <div className="w-12 h-12 rounded-xl bg-aqua/10 flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-aqua" />
            </div>
            <h3 className="font-semibold text-white mb-2">Brotherhood</h3>
            <p className="text-gray-400 text-sm">
              Connect with Christian dads who understand your challenges
            </p>
          </div>
          <div className="bg-charcoal-light rounded-xl p-6 border border-charcoal-lighter">
            <div className="w-12 h-12 rounded-xl bg-aqua/10 flex items-center justify-center mb-4">
              <Target className="w-6 h-6 text-aqua" />
            </div>
            <h3 className="font-semibold text-white mb-2">Focus Areas</h3>
            <p className="text-gray-400 text-sm">
              Marriage, Parenting, Work - grow where you need it most
            </p>
          </div>
          <div className="bg-charcoal-light rounded-xl p-6 border border-charcoal-lighter">
            <div className="w-12 h-12 rounded-xl bg-aqua/10 flex items-center justify-center mb-4">
              <Calendar className="w-6 h-6 text-aqua" />
            </div>
            <h3 className="font-semibold text-white mb-2">Weekly Rhythm</h3>
            <p className="text-gray-400 text-sm">
              Live calls, focus prompts, and celebrating wins together
            </p>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-6 mt-16">
          <div className="bg-charcoal-light rounded-xl p-6 border border-charcoal-lighter">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-aqua/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-aqua" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Our Mission</h3>
            </div>
            <p className="leading-relaxed text-muted-foreground">
              Dad Partners brings together fathers with children at home who want
              real brotherhood as they navigate the pressures of parenting,
              marriage, and work. Through honest conversation, shared wisdom,
              and support from other dads, members grow stronger together.
            </p>
          </div>
          <div className="bg-charcoal-light rounded-xl p-6 border border-charcoal-lighter">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-aqua/10 flex items-center justify-center">
                <Compass className="w-5 h-5 text-aqua" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Our Vision</h3>
            </div>
            <p className="leading-relaxed text-muted-foreground">
              A world where every Christian dad has a band of brothers walking alongside him,
              helping him become the husband and father God designed him to be.
            </p>
          </div>
        </div>

        {/* Guest Guides Section */}
        {guestGuides && guestGuides.length > 0 && (
          <div className="mt-16">
            <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Mic className="w-6 h-6 text-aqua" />
                <h3 className="text-2xl font-bold text-foreground">Guide Partners</h3>
              </div>
              <p className="text-muted-foreground">Trustworthy men and women sharing from failure, faith, and transformation. Voices willing to speak honestly about what didn&apos;t work-and what changed.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {guestGuides.map((guide) => (
                <div
                  key={guide.guide_id || guide.user_id || guide.email || guide.name}
                  className="bg-charcoal-light rounded-xl p-4 border border-charcoal-lighter text-center hover:border-aqua/50 transition-colors"
                >
                  <Avatar className="w-20 h-20 mx-auto mb-3">
                    <AvatarImage src={guide.photo_url || guide.picture} alt={guide.name} />
                    <AvatarFallback className="bg-aqua/20 text-aqua text-xl">
                      {guide.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <h4 className="text-sm font-semibold text-foreground">{guide.name}</h4>
                  {guide.bio && (
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{guide.bio}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-charcoal-lighter">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-6">
              <a href="/terms" className="hover:text-aqua transition-colors">Terms of Service</a>
              <a href="/privacy" className="hover:text-aqua transition-colors">Privacy Policy</a>
            </div>
            <span>© {new Date().getFullYear()} Dad Partners. All rights reserved.</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LoginPage;
